import { RefObject, useLayoutEffect } from 'react';
import type { ScheduleComponent } from '@syncfusion/ej2-react-schedule';

const BORDI_E_PADDING = [
  'borderTopWidth',
  'borderBottomWidth',
  'paddingTop',
  'paddingBottom',
] as const;

// Altezza del contenuto, misurata come il contentRect del ResizeObserver: senza
// bordi e padding.
const getAltezzaContenuto = (elemento: HTMLElement): number => {
  const stile = getComputedStyle(elemento);
  return BORDI_E_PADDING.reduce(
    (altezza, proprieta) => altezza - (parseFloat(stile[proprieta]) || 0),
    elemento.getBoundingClientRect().height
  );
};

/**
 * Riallinea lo Scheduler quando cambia l'altezza del suo contenitore senza un resize
 * della finestra: la fascia Presenze che si accende, si spegne o cambia modalità, o
 * che cresce e cala con le sue chip (arrivo dei dati, chip che vanno a capo).
 * Syncfusion calcola l'altezza dell'area delle ore solo al render e
 * al resize della finestra, e senza refreshLayout l'area sborderebbe sotto il
 * contenitore. Lo usano le agende con la fascia sopra il calendario: agenda negozio
 * e giornaliero studio.
 *
 * Il riferimento è l'altezza misurata al montaggio, in un layout effect: gira dopo il
 * componentDidMount di ScheduleComponent, che è figlio del componente che usa l'hook,
 * quindi legge lo stesso layout su cui Syncfusion ha calcolato l'area delle ore. Così
 * anche una fascia che cambia altezza prima della prima osservazione (dati arrivati
 * uno o due frame dopo il montaggio dello Scheduler, come nel giornaliero studio che
 * si rimonta a ogni giorno nuovo) porta al riallineamento. Alla prima osservazione
 * conta solo un cambio di almeno 1px (arrotondamenti fra le due misure), poi si
 * confronta con l'osservazione precedente: un aggiornamento dei dati che non cambia
 * l'altezza non chiude il quick info e non ridisegna il calendario. Più cambi nello
 * stesso frame (le chiamate della fascia che arrivano insieme) diventano un solo
 * refreshLayout.
 *
 * Durante un resize della finestra non si fa nulla: il gestore del resize di
 * Syncfusion ha già ricalcolato l'area delle ore nello stesso frame (l'evento resize
 * arriva prima delle osservazioni del ResizeObserver). refreshLayout ricostruisce
 * anche l'editor degli appuntamenti, e ripeterlo a ogni frame mentre si trascina il
 * bordo della finestra farebbe scattare il calendario. Il resize si riconosce
 * dall'evento della finestra, che accende un segnale spento al frame dopo: così
 * anche un resize che non cambia l'altezza del calendario non lascia valori vecchi
 * che farebbero saltare il riallineamento successivo dovuto alla fascia. Se il resize
 * fa andare a capo le chip della fascia allineata alle colonne, il cambio d'altezza
 * arriva dopo che le colonne sono state rimisurate (useColonneGiorni, al frame
 * successivo), a segnale già spento: il riallineamento c'è. Nella fascia a tutta
 * larghezza (Agenda, giornaliero studio) le chip vanno a capo nello stesso layout
 * che il gestore del resize di Syncfusion legge: l'area delle ore è già giusta.
 */
export function useSchedulerLayoutRefresh(
  scheduleObj: RefObject<ScheduleComponent>,
  scheduleElementId: string
) {
  useLayoutEffect(() => {
    const elemento = document.getElementById(scheduleElementId);
    if (elemento === null || typeof ResizeObserver === 'undefined') return;

    let altezzaPrecedente = getAltezzaContenuto(elemento);
    let primaOsservazione = true;
    let resizeFinestra = false;
    let frameResize = 0;
    let frame = 0;

    const onResize = () => {
      resizeFinestra = true;
      cancelAnimationFrame(frameResize);
      // Due frame: quello chiesto durante il resize gira nello stesso frame, PRIMA
      // delle notifiche del ResizeObserver, e da solo spegnerebbe il segnale troppo
      // presto. Il segnale si spegne quindi nel frame successivo.
      frameResize = requestAnimationFrame(() => {
        frameResize = requestAnimationFrame(() => {
          resizeFinestra = false;
        });
      });
    };
    window.addEventListener('resize', onResize);

    const osservatore = new ResizeObserver((entries) => {
      const altezza = entries[entries.length - 1].contentRect.height;
      const cambiata = primaOsservazione
        ? Math.abs(altezza - altezzaPrecedente) >= 1
        : altezza !== altezzaPrecedente;
      primaOsservazione = false;
      altezzaPrecedente = altezza;
      if (!cambiata || resizeFinestra) return;

      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const schedule = scheduleObj.current;
        // Lo Scheduler può essere già stato distrutto (cambio di data o di vista).
        if (schedule && !schedule.isDestroyed) schedule.refreshLayout();
      });
    });
    osservatore.observe(elemento);

    return () => {
      window.removeEventListener('resize', onResize);
      cancelAnimationFrame(frameResize);
      cancelAnimationFrame(frame);
      osservatore.disconnect();
    };
  }, [scheduleObj, scheduleElementId]);
}
